import { useId } from "react";
import { Link } from "react-router";

import { OndoMark } from "../lib/OndoMark.tsx";
import { PATHS } from "../routes/routes.constants.ts";
import { WitnessLines } from "../witness/WitnessLines.tsx";
import { formatWitnessMoment } from "../witness/witness.model.ts";
import { PROOF_COPY } from "./proof.constants.ts";
import { useProofController } from "./useProofController.ts";
import type { HourView, ProofReport } from "./proof.model.ts";
import type { ProofController } from "./useProofController.ts";
import "../witness/witness.css";
import "./proof.css";

/**
 * The proof page, for whoever holds a bundle.
 *
 * Outside both route gates, like the viewing room, and for a plainer reason:
 * it reads no data from ONDO at all. Everything on it is either copy or what
 * the reader's own file said once their own machine had hashed it.
 *
 * THE CHECK COMES FIRST AND THE METHOD IS FOLDED. A reader with a bundle wants
 * one sentence; the arithmetic behind it and the way to redo it with no ONDO
 * are both on the page, in `<details>`, so they are found by whoever looks and
 * never in the way of whoever does not. Nothing is hidden by JavaScript: a
 * reader with it off can still open every fold.
 */
export function ProofCheck() {
  const controller = useProofController();
  const { view } = controller;

  return (
    <main className="proof">
      <div className="proof__frame">
        <Link className="proof__brand" to={PATHS.root} aria-label={PROOF_COPY.backToOndo}>
          <OndoMark />
        </Link>

        <header className="proof__head">
          <h1 className="proof__title">{PROOF_COPY.title}</h1>
          <p className="proof__lead">{PROOF_COPY.lead}</p>
        </header>

        <section className="proof__check" aria-label={PROOF_COPY.title}>
          {(view.kind === "idle" || view.kind === "reading") && (
            <Intake controller={controller} busy={view.kind === "reading"} />
          )}

          {view.kind === "reading" && (
            <p className="proof__quiet" role="status">
              {view.message}
            </p>
          )}
          {view.kind === "invalid" && (
            <div className="proof__verdict proof__verdict--problems" role="alert">
              <p className="proof__verdict-title">{view.title}</p>
              <p className="proof__verdict-detail">{view.reason}</p>
            </div>
          )}
          {view.kind === "checked" && (
            <Report report={view.report} onDownloadReceipt={controller.downloadReceipt} />
          )}
          {(view.kind === "checked" || view.kind === "invalid") && (
            <button type="button" className="proof__button" onClick={controller.reset}>
              {PROOF_COPY.clear}
            </button>
          )}
        </section>

        <section className="proof__section" aria-labelledby="proof-how">
          <h2 id="proof-how" className="proof__h2">
            {PROOF_COPY.howTitle}
          </h2>
          {PROOF_COPY.how.map((paragraph) => (
            <p key={paragraph.slice(0, 24)} className="proof__body">
              {paragraph}
            </p>
          ))}
          <details className="proof__fold">
            <summary className="proof__fold-summary">{PROOF_COPY.howMoreTitle}</summary>
            <div className="proof__fold-body">
              {PROOF_COPY.howMore.map((paragraph) => (
                <p key={paragraph.slice(0, 24)} className="proof__body">
                  {paragraph}
                </p>
              ))}
            </div>
          </details>
        </section>

        <section className="proof__section" aria-labelledby="proof-without">
          <details className="proof__fold">
            <summary className="proof__fold-summary proof__fold-summary--h2">
              <h2 id="proof-without" className="proof__h2">
                {PROOF_COPY.withoutTitle}
              </h2>
            </summary>
            <div className="proof__fold-body">
              <p className="proof__body">{PROOF_COPY.withoutLead}</p>
              <ol className="proof__steps">
                {PROOF_COPY.steps.map((step) => (
                  <li key={step.title} className="proof__step">
                    <p className="proof__step-title">{step.title}</p>
                    <p className="proof__body">{step.body}</p>
                    {step.command && <pre className="proof__command">{step.command}</pre>}
                  </li>
                ))}
              </ol>
              <p className="proof__body">
                <a className="proof__link" href={PROOF_COPY.otsLink.href} rel="noreferrer">
                  {PROOF_COPY.otsLink.label}
                </a>
              </p>
            </div>
          </details>
        </section>

        <p className="proof__limit">{PROOF_COPY.limit}</p>
      </div>
    </main>
  );
}

/** The file picker, and the paste box behind it. */
function Intake({ controller, busy }: { controller: ProofController; busy: boolean }) {
  const fileId = useId();
  const pasteId = useId();

  return (
    <form
      className="proof__intake"
      onSubmit={(event) => {
        event.preventDefault();
        controller.checkPaste();
      }}
    >
      <label className="proof__drop" htmlFor={fileId}>
        <span className="proof__drop-label">{PROOF_COPY.dropLabel}</span>
        <span className="proof__drop-hint">{PROOF_COPY.dropHint}</span>
        <input
          id={fileId}
          className="proof__file"
          type="file"
          accept="application/json,.json"
          disabled={busy}
          onChange={(event) => controller.checkFile(event.target.files?.[0] ?? null)}
        />
      </label>

      {controller.pasting ? (
        <>
          <label className="proof__label" htmlFor={pasteId}>
            {PROOF_COPY.pasteLabel}
          </label>
          <textarea
            id={pasteId}
            className="proof__paste"
            rows={6}
            spellCheck={false}
            value={controller.pasted}
            onChange={(event) => controller.typePaste(event.target.value)}
          />
          <button type="submit" className="proof__button" disabled={busy}>
            {PROOF_COPY.pasteAction}
          </button>
        </>
      ) : (
        <button type="button" className="proof__textlink" onClick={controller.showPaste}>
          {PROOF_COPY.pasteToggle}
        </button>
      )}

      <p className="proof__privacy">{PROOF_COPY.privacy}</p>
    </form>
  );
}

function Report({
  report,
  onDownloadReceipt,
}: {
  report: ProofReport;
  onDownloadReceipt: (hour: HourView) => void;
}) {
  return (
    <div className="proof__report">
      <div className={`proof__verdict proof__verdict--${report.verdict.kind}`} role="status">
        <p className="proof__verdict-title">{report.verdict.title}</p>
        <p className="proof__verdict-detail">{report.verdict.detail}</p>
        {report.verdict.note && <p className="proof__verdict-note">{report.verdict.note}</p>}
      </div>

      <dl className="proof__facts">
        <dt>{PROOF_COPY.forWork}</dt>
        <dd>
          <code>{report.workId}</code>
        </dd>
        {report.exportedAt && (
          <>
            <dt>{PROOF_COPY.exported}</dt>
            <dd>{formatWitnessMoment(report.exportedAt)}</dd>
          </>
        )}
      </dl>

      <h3 className="proof__h3">{PROOF_COPY.entriesTitle}</h3>
      <p className="proof__quiet">{PROOF_COPY.entriesLead}</p>
      <WitnessLines lines={report.lines} />

      <details className="proof__fold">
        <summary className="proof__fold-summary">{PROOF_COPY.hoursTitle}</summary>
        <div className="proof__fold-body">
          <p className="proof__body">{PROOF_COPY.hoursLead}</p>
          <ol className="proof__hours">
            {report.hours.map((hour) => (
              <li key={hour.periodStart} className={`proof__hour proof__hour--${hour.chain}`}>
                <p className="proof__hour-when">
                  {formatWitnessMoment(hour.periodStart)} · {hour.entries}{" "}
                  {PROOF_COPY.hourEntries}
                </p>
                <p className="proof__hour-anchor">
                  {hour.anchor.kind === "anchored"
                    ? `${PROOF_COPY.anchor.anchored} ${hour.anchor.blockHeight.toLocaleString("en-GB")}`
                    : PROOF_COPY.anchor[hour.anchor.kind]}
                </p>
                <p className={`proof__hour-chain proof__hour-chain--${hour.chain}`}>
                  {PROOF_COPY.chain[hour.chain]}
                </p>
                {hour.root && (
                  <p className="proof__hour-root">
                    {PROOF_COPY.root} <code>{hour.root}</code>
                  </p>
                )}
                {hour.receipt && hour.verifyCommand && (
                  <div className="proof__hour-actions">
                    <button
                      type="button"
                      className="proof__button"
                      onClick={() => onDownloadReceipt(hour)}
                    >
                      {PROOF_COPY.downloadReceipt}
                    </button>
                    <p className="proof__quiet">{PROOF_COPY.verifyHint}</p>
                    <pre className="proof__command">{hour.verifyCommand}</pre>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      </details>
    </div>
  );
}
